import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ArrowLeft,
  ArrowRight,
  Users,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle2,
  Phone,
  User,
  Mail,
  Lock,
} from "lucide-react";
import { useCoreController } from "@/services/coreController";
import { analyticsService } from "@/services/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authService } from "@/services/authService";
import useFindUser from "@/hooks/useFindUser";
import { isValidPrefix } from "@/constants";
import { Notification } from "@/components/Notification";
import { cn } from "@/lib/utils";

const step1Schema = z.object({
  firstName: z.string().min(1, "Veuillez entrer un prénom"),
  lastName: z.string().min(1, "Veuillez entrer un nom"),
  phoneNumber: z
    .string()
    .min(8, "Le numéro doit contenir 8 chiffres")
    .refine((phone) => isValidPrefix(phone), {
      message: "Le numéro doit commencer par un préfixe valide (70, 74, ...)",
    }),
});

const step2Schema = z.object({
  email: z.string().email("Email invalide"),
  password: z.string().min(6, "Le mot de passe doit contenir au moins 6 caractères"),
  confirmPassword: z.string().min(6, "Confirmez le mot de passe"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Les mots de passe ne correspondent pas",
  path: ["confirmPassword"],
});

type Step1Values = z.infer<typeof step1Schema>;
type Step2Values = z.infer<typeof step2Schema>;

export function RegisterPage() {
  const navigate = useNavigate();
  const coreController = useCoreController();
  const { setUser } = useFindUser();
  const [step, setStep] = useState<1 | 2>(1);
  const [step1Data, setStep1Data] = useState<Step1Values | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [notification, setNotification] = useState<{
    isVisible: boolean;
    message: string;
    type: "error" | "success";
  }>({ isVisible: false, message: "", type: "error" });

  const form1 = useForm<Step1Values>({
    resolver: zodResolver(step1Schema),
    defaultValues: { firstName: "", lastName: "", phoneNumber: "" },
  });

  const form2 = useForm<Step2Values>({
    resolver: zodResolver(step2Schema),
    defaultValues: { email: "", password: "", confirmPassword: "" },
  });

  const getThemeColor = () => {
    switch (coreController.compte.type) {
      case "1XBET":
        return "#002366";
      case "BETWINNER":
        return "#22c55e";
      case "MELBET":
        return "#eab308";
      default:
        return "#002366";
    }
  };

  const themeColor = getThemeColor();

  const showNotification = (
    message: string,
    type: "error" | "success" = "error"
  ) => {
    setNotification({ isVisible: true, message, type });
  };

  const hideNotification = () => {
    setNotification((prev) => ({ ...prev, isVisible: false }));
  };

  const handleStep1 = (data: Step1Values) => {
    setStep1Data(data);
    setStep(2);
  };

  const onSubmit = async (data: Step2Values) => {
    if (!step1Data) return;
    setIsLoading(true);
    try {
      const response = await authService.register({
        email: data.email,
        password: data.password,
        phoneNumber: `226${step1Data.phoneNumber}`,
        firstName: step1Data.firstName,
        lastName: step1Data.lastName,
      });

      if (!response?.success || !response.user) {
        showNotification(
          response?.error?.message || "Une erreur est survenue lors de l'inscription"
        );
        setIsLoading(false);
        return;
      }

      setUser({
        id: response.user.id,
        email: response.user.email,
        first_name: response.user.displayName?.split(' ')[0],
        last_name: response.user.displayName?.split(' ').slice(1).join(' '),
        phone_number: response.user.phoneNumber,
        balance: response.user.balance,
      });

      analyticsService.trackEvent("register_complete");
      showNotification("Compte créé avec succès", "success");
      navigate("/dashboard", { replace: true });
      window.location.reload();
    } catch (error: any) {
      showNotification(error.message || "Une erreur est survenue");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="min-h-screen bg-gray-50"
    >
      {/* App Bar */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center">
          <button
            onClick={() => (step === 1 ? navigate("/") : setStep(1))}
            className="p-2 rounded-full bg-white shadow-md hover:shadow-lg transition-shadow"
            style={{ color: themeColor }}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="flex-1 text-center text-lg font-semibold" style={{ color: themeColor }}>
            Créer mon lien de parrainage
          </h1>
          <div className="w-9" />
        </div>
      </div>

      <div className="max-w-md mx-auto px-5 py-6">
        {/* Stepper */}
        <div className="flex items-center justify-center gap-4 mb-6">
          <div className="flex flex-col items-center gap-1">
            <div
              className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center text-white font-bold",
                step >= 1 ? "" : "bg-gray-300"
              )}
              style={{ backgroundColor: step >= 1 ? themeColor : undefined }}
            >
              1
            </div>
            <span className="text-xs text-gray-500">Identité</span>
          </div>
          <div className="h-1 w-12 rounded-full bg-gray-200">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: step >= 2 ? "100%" : "0%",
                backgroundColor: themeColor,
              }}
            />
          </div>
          <div className="flex flex-col items-center gap-1">
            <div
              className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center text-white font-bold",
                step >= 2 ? "" : "bg-gray-300"
              )}
              style={{ backgroundColor: step >= 2 ? themeColor : undefined }}
            >
              2
            </div>
            <span className="text-xs text-gray-500">Connexion</span>
          </div>
        </div>

        {step === 1 ? (
          <form onSubmit={form1.handleSubmit(handleStep1)} className="space-y-5">
            <div className="rounded-2xl p-5 border bg-white shadow-sm space-y-5">
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-800">Prénom(s)</Label>
                <div className="relative">
                  <div
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-lg"
                    style={{ backgroundColor: `${themeColor}1A` }}
                  >
                    <User className="w-5 h-5" style={{ color: themeColor }} />
                  </div>
                  <Input
                    {...form1.register("firstName")}
                    placeholder="Votre prénom"
                    className="pl-16 py-6 text-base"
                  />
                </div>
                {form1.formState.errors.firstName && (
                  <p className="text-sm text-red-600">{form1.formState.errors.firstName.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-800">Nom</Label>
                <div className="relative">
                  <div
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-lg"
                    style={{ backgroundColor: `${themeColor}1A` }}
                  >
                    <User className="w-5 h-5" style={{ color: themeColor }} />
                  </div>
                  <Input
                    {...form1.register("lastName")}
                    placeholder="Votre nom"
                    className="pl-16 py-6 text-base"
                  />
                </div>
                {form1.formState.errors.lastName && (
                  <p className="text-sm text-red-600">{form1.formState.errors.lastName.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-800">Numéro de téléphone</Label>
                <div className="relative">
                  <div
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-lg"
                    style={{ backgroundColor: `${themeColor}1A` }}
                  >
                    <Phone className="w-5 h-5" style={{ color: themeColor }} />
                  </div>
                  <div className="flex items-center w-full pl-16 pr-4 py-4 rounded-xl border-2 border-gray-300 bg-white focus-within:border-[var(--theme-color)]"
                    style={{ "--theme-color": themeColor } as any}
                  >
                    <span className="text-gray-500 font-medium mr-2">+226</span>
                    <input
                      {...form1.register("phoneNumber")}
                      type="tel"
                      inputMode="numeric"
                      placeholder="70766767"
                      className="flex-1 outline-none text-base"
                    />
                  </div>
                </div>
                {form1.formState.errors.phoneNumber && (
                  <p className="text-sm text-red-600">{form1.formState.errors.phoneNumber.message}</p>
                )}
              </div>
            </div>

            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              type="submit"
              className="w-full py-4 rounded-xl text-white font-semibold text-base flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all"
              style={{ backgroundColor: themeColor }}
            >
              <span>Continuer</span>
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </form>
        ) : (
          <form onSubmit={form2.handleSubmit(onSubmit)} className="space-y-5">
            <div className="rounded-2xl p-5 border bg-white shadow-sm space-y-5">
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-800">Email</Label>
                <div className="relative">
                  <div
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-lg"
                    style={{ backgroundColor: `${themeColor}1A` }}
                  >
                    <Mail className="w-5 h-5" style={{ color: themeColor }} />
                  </div>
                  <Input
                    {...form2.register("email")}
                    type="email"
                    placeholder="m@example.com"
                    className="pl-16 py-6 text-base"
                  />
                </div>
                {form2.formState.errors.email && (
                  <p className="text-sm text-red-600">{form2.formState.errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-800">Mot de passe</Label>
                <div className="relative">
                  <div
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-lg"
                    style={{ backgroundColor: `${themeColor}1A` }}
                  >
                    <Lock className="w-5 h-5" style={{ color: themeColor }} />
                  </div>
                  <Input
                    {...form2.register("password")}
                    type={showPassword ? "text" : "password"}
                    placeholder="Choisissez un mot de passe"
                    className="pl-16 pr-12 py-6 text-base"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {form2.formState.errors.password && (
                  <p className="text-sm text-red-600">{form2.formState.errors.password.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-800">Confirmer le mot de passe</Label>
                <div className="relative">
                  <div
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-lg"
                    style={{ backgroundColor: `${themeColor}1A` }}
                  >
                    <Lock className="w-5 h-5" style={{ color: themeColor }} />
                  </div>
                  <Input
                    {...form2.register("confirmPassword")}
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirmez le mot de passe"
                    className="pl-16 pr-12 py-6 text-base"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {form2.formState.errors.confirmPassword && (
                  <p className="text-sm text-red-600">{form2.formState.errors.confirmPassword.message}</p>
                )}
              </div>
            </div>

            <div className="rounded-xl p-4 bg-blue-50 border border-blue-100">
              <p className="text-sm text-blue-700">
                Ces identifiants vous permettront de consulter votre espace parrain et votre solde à tout moment.
              </p>
            </div>

            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              type="submit"
              disabled={isLoading}
              className="w-full py-4 rounded-xl text-white font-semibold text-base flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: themeColor }}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <span>Créer mon compte</span>
                  <CheckCircle2 className="w-5 h-5" />
                </>
              )}
            </motion.button>
          </form>
        )}

        <div className="mt-6 text-center text-sm text-gray-600">
          <p className="mb-2">Vous souhaitez être partenaire</p>
          <button
            onClick={() => navigate("/login")}
            className="font-semibold underline"
            style={{ color: themeColor }}
          >
            Se connecter
          </button>
        </div>
      </div>

      <Notification
        message={notification.message}
        type={notification.type}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />
    </motion.div>
  );
}

export default RegisterPage;
